import React from 'react';

import * as S from './styles';

interface IOption {
  /** The value handed back to `onChange`. */
  id: string | number;
  title: string;
}

interface IProps {
  options: IOption[];
  /** The selected `id`. Matched with `===`, so keep the types consistent. */
  value: string | number;
  onChange: (id: string | number) => void;
  className?: string;
  style?: object;
}

/**
 * The design's `.segmented` — a small set of mutually exclusive filters that
 * belongs in a toolbar beside the search box.
 *
 * It replaces the row of dark grey pill buttons two list screens drew for
 * themselves: `#8a93ac` rounded rectangles that went `--primary-dark-two`
 * when selected, so the unselected options were *louder* than anything else
 * in the card and the selected one was distinguished only by being a
 * different dark colour. A segmented control says the same thing the other
 * way round — the group is quiet, and the chosen segment is the one lifted
 * onto the surface.
 *
 * Use it only for a filter over a closed, short list of states. Anything
 * longer belongs in the filter popup.
 */
const Segmented: React.FC<IProps> = ({ options, value, onChange, className, style }) => (
  <S.Segmented className={className} style={style} role="group">
    {options.map((option) => (
      <S.Segment
        key={option.id}
        type="button"
        className={option.id === value ? 'on' : ''}
        aria-pressed={option.id === value}
        onClick={() => onChange(option.id)}
      >
        {option.title}
      </S.Segment>
    ))}
  </S.Segmented>
);

export default Segmented;
